import { defineStore } from 'pinia'
import { computed } from 'vue'
import { useConfigStore } from '@dashboard/stores/configStore'
import { storeToRefs } from 'pinia'
import { klona } from 'klona'
import { makeId, idPrefixes, uuid } from '@dashboard/services/uuid'
import { type Flow, flowSchema } from '@dashboard/types/config'
import { funnelDefinitionEach } from '@dashboard/modules/flow/services/funnelDefinitions'
import { useWidgetStore } from '@dashboard/modules/widget/stores/widgetStore'
import { AnalyticsEventTypes, recordAnalyticsEvent } from '@dashboard/services/analytics'

export const useFlowStore = defineStore('flow', () => {
  const configStore = useConfigStore()
  const { config } = storeToRefs(configStore)

  const flows = computed(() => config.value?.rightCta?.offerFunnels || [])
  const getById = (id: string) => flows.value.find((funnel) => funnel.id === id)

  const create = (flow: Partial<Flow>) => {
    // const newFlow = flowSchema.parse({
    //   id: makeId(idPrefixes.FLOW),
    //   name: `New Flow ${flows.value.length + 1}`,
    //   funnelDefinition: { _uuid: uuid(), type: 'root', child: [] },
    //   ...flow
    // }) as Flow

    const newFlow = {
      id: makeId(idPrefixes.FLOW),
      name: `New Flow ${flows.value.length + 1}`,
      funnelDefinition: { _uuid: uuid(), type: 'root', child: [] },
      ...flow
    } as Flow

    flows.value.push(newFlow)

    void recordAnalyticsEvent(AnalyticsEventTypes.FLOW_CREATED, {
      id: newFlow.id,
      name: newFlow.name
    })

    return newFlow
  }

  const destroy = (flow: Flow) => {
    const foundIndex = flows.value.findIndex((f) => Object.is(f, flow))
    if (foundIndex === -1) {
      return
    } // this should really never happen

    void recordAnalyticsEvent(AnalyticsEventTypes.FLOW_DELETED, {
      id: flow.id,
      name: flow.name
    })

    const widgetStore = useWidgetStore()
    const widgets = widgetStore.getWidgetsForOfferFunnel(flow.id)
    widgets.forEach((widget) => {
      widget.contentId = null
    })
    flows.value.splice(foundIndex, 1)
  }

  const duplicate = (flow: Flow) => {
    const newFlow = {
      ...klona(flow),
      id: makeId(idPrefixes.FLOW),
      name: `${flow.name} (Copy)`,
      // The copy is new: the server stamps it on save instead of inheriting the source's time.
      modifiedAt: undefined
    }

    funnelDefinitionEach(newFlow.funnelDefinition, (node: any) => {
      node._uuid = uuid()
    })

    flows.value.push(newFlow)

    return newFlow
  }

  return {
    flows,
    getById,
    create,
    destroy,
    duplicate
  }
})
